import React, { FunctionComponent, useState, useEffect, useMemo } from "react";
import { useTable, usePagination, useFilters } from "react-table";
import {
  GridStyled,
  RowsSelectPanel,
  ActionsPanel,
  TablePanel,
  TableContainer,
  PaginationPanel,
  InputFilter,
  SelectFilter,
  TableButton,
} from "./styled.components";
import { useSelector } from "react-redux";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

import LottieLoader from "react-lottie-loader";

type GridProps = {
  children?: any;
  columns: any;
  data: any;
  config?: any;
  isLoading?: boolean;
  handleActionClick?: (e: any, action: string) => void;
};

const Grid: FunctionComponent<GridProps> = ({
  children,
  columns,
  data,
  config,
  isLoading = false,
  handleActionClick,
}) => {
  const [dataMap, setDataMap] = useState<any[]>([]);
  const { header } = useSelector((state: any) => state.theme);
  const loader = require("../../data/loader_animation.json");

  useEffect(() => {
    setDataMap(formatedData());
  }, [data]);

  const handleRenderOptions = (action: any, item: any) => {
    let color = action.color;
    let icon = action.icon;
    if (action.isStatus && action.isStatus !== "") {
      if (!item[action.isStatus]) {
        color = action.colorNegative;
        icon = action.iconNegative;
      }
    }
    if (action.visible) {
      if (action?.hiddenStatus === true) {
        if (action?.keyHidden && item[action?.keyHidden] === false) return null;
      }
      return (
        <div className={"content-button"}>
          <TableButton
            color={color}
            visible={action.visible}
            onClick={() => {
              action.handle
                ? action.handle(item)
                : handleActionClick && handleActionClick(item, action.action);
            }}
          >
            {action.tooltip && action.tooltip != "" ? (
              <Tooltip
                title={action.tooltip}
                enterDelay={800}
                placement="top"
                style={{ fontFamily: "MontserratRegular" }}
              >
                <Icon style={{ fontSize: "clamp(0.7rem, 2.5vw, 1.2rem)" }}>
                  {icon}
                </Icon>
              </Tooltip>
            ) : (
              <Icon style={{ fontSize: "clamp(0.7rem, 2.5vw, 1.2rem)" }}>
                {icon}
              </Icon>
            )}
          </TableButton>
        </div>
      );
    } else {
      return <></>;
    }
  };

  const formatedColumns = useMemo(() => {
    const actionsHeaders = config.actions
      .filter((action: any) => action.visible)
      .map((action: any) => {
        return {
          accessor: "btn" + action.action,
          Header: "",
        };
      });
    const newColumns = [...columns, ...actionsHeaders];
    return newColumns;
  }, []);

  const formatedData = () => {
    return data.map((item: any) => {
      config.actions.forEach((action: any) => {
        item["btn" + action.action] = handleRenderOptions(action, item);
      });
      return item;
    });
  };

  const defaultColumn = useMemo(
    () => ({
      Filter: EmptyFilter,
    }),
    []
  );

  const filterTypes = useMemo(
    () => ({
      text: (rows: any, id: any, filterValue: any) => {
        return rows.filter((row: any) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: formatedColumns,
      data: dataMap,
      initialState: { pageIndex: 0 },
      defaultColumn,
      filterTypes,
    },
    useFilters,
    usePagination
  );

  return (
    <>
      <GridStyled>
        {isLoading ? null : (
          <RowsSelectPanel>
            Registros por página{" "}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[5, 10, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </RowsSelectPanel>
        )}
        {isLoading ? null : <ActionsPanel>{children}</ActionsPanel>}
        <TablePanel>
          {isLoading ? (
            <div
              style={{
                width: "100%",
                height: "500px",
                display: "flex",
                justifyContent: "center",
                justifyItems: "center",
              }}
            >
              <LottieLoader animationData={loader} className={"animation"} />
            </div>
          ) : (
            <>
              <TableContainer>
                <table {...getTableProps()}>
                  <thead>
                    {headerGroups.map((headerGroup: any) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column: any) => (
                          <th
                            {...column.getHeaderProps()}
                            style={{
                              backgroundColor:
                                column.Header === ""
                                  ? "transparent"
                                  : header?.gridHeader,
                              color:
                                column.Header === ""
                                  ? "transparent"
                                  : header?.gridTextHeader,
                            }}
                            className={
                              column.Header === "" ? "cellNoBorder" : ""
                            }
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                    {headerGroups.map((headerGroup: any) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column: any) => (
                          <th
                            {...column.getHeaderProps()}
                            style={{
                              backgroundColor:
                                column.Header === ""
                                  ? "transparent"
                                  : header?.gridHeader,
                            }}
                            className={
                              column.Header === "" ? "cellNoBorder" : ""
                            }
                          >
                            {
                              <div>
                                {" "}
                                {column.canFilter
                                  ? column.render("Filter")
                                  : null}
                              </div>
                            }
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map((row: any) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell: any) => {
                            return (
                              <td
                                {...cell.getCellProps()}
                                className={
                                  cell.column.Header === ""
                                    ? "cellNoBorder"
                                    : ""
                                }
                                style={{ color: "#707070" }}
                              >
                                {cell.render("Cell")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableContainer>
              <PaginationPanel>
                <div className={"pagination"}>
                  <button
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    {"Anterior"}
                  </button>{" "}
                  <div className={"pagesCount"}>
                    <span>
                      Página {pageIndex + 1} de {pageOptions.length}{" "}
                    </span>
                  </div>
                  <button onClick={() => nextPage()} disabled={!canNextPage}>
                    {"Siguiente"}
                  </button>{" "}
                </div>
              </PaginationPanel>
            </>
          )}
        </TablePanel>
      </GridStyled>
    </>
  );
};

export const EmptyFilter = ({
  column: { filterValue, preFilteredRows, setFilter },
}: {
  column: {
    filterValue: any;
    preFilteredRows: any;
    setFilter: any;
  };
}) => {
  return <></>;
};

export const TextFilter = ({
  column: { filterValue, preFilteredRows, setFilter },
}: {
  column: {
    filterValue: any;
    preFilteredRows: any;
    setFilter: any;
  };
}) => {
  return (
    <InputFilter
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    />
  );
};

export const NumberFilter = ({
  column: { filterValue, preFilteredRows, setFilter },
}: {
  column: {
    filterValue: any;
    preFilteredRows: any;
    setFilter: any;
  };
}) => {
  return (
    <InputFilter
      value={filterValue || ""}
      type={"number"}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    />
  );
};

export const SelectColumnFilter = ({
  column: { filterValue, setFilter, preFilteredRows, id },
}: {
  column: {
    filterValue: any;
    preFilteredRows: any;
    setFilter: any;
    id: number;
  };
}) => {
  const options = useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row: any) => {
      options.add(row.values[id]);
    });
    //@ts-ignore
    return [...options.values()];
  }, [id, preFilteredRows]);

  return (
    <SelectFilter
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">All</option>
      {options.map((option: any, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </SelectFilter>
  );
};

export default Grid;
